// Copyright © 2019-2026 Focal Systems. All rights reserved.

// Resolves a store's opening hours for a given day from the `store_opening_hours` rows the
// details endpoint returns, then narrows that to a single window for a schedule exception.
//
// The priority ladder mirrors `StoreOpeningHoursDao.resolve_for_store_and_date` in backend-main
// one-for-one — if that changes, this has to change with it.

import type { components } from "@/api/schema";
import { compileRuleDays } from "./rule-days";
import type { CaptureWindow } from "./schedule-utils";

export type OpeningHoursEntry = components["schemas"]["StoresDetailsOpeningHoursResponse"];

/** How far ahead to look for the days a rule can apply on — a full year covers every date shape. */
const HORIZON_DAYS = 366;

/** A resolved day: the open window, or `null` when the store is closed all day. */
export interface ResolvedDay {
  window: CaptureWindow | null;
  /** The matched row's note (e.g. "Christmas Day"), when it had one. */
  description?: string;
}

/** Parse `HH:MM[:SS]` into minutes since midnight. */
function timeToMinutes(value: string | undefined): number | null {
  if (!value) return null;
  const [h, m] = value.split(":");
  const hh = Number(h);
  const mm = Number(m);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
}

const SHORT_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** `MM/DD`, the format of the `dayOfYear` column. */
function dayOfYearOf(date: Date): string {
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
}

/** `YYYY-MM-DD` in local time — `toISOString()` would shift the date across a timezone. */
function isoDateOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Priority of an entry for a date, highest first, or 0 when it doesn't apply. Store-level beats
 * chain-level within each tier: date override > annual override > weekday > default.
 */
function priorityFor(entry: OpeningHoursEntry, date: Date): number {
  const isStore = entry.scope === "store";
  const isDate = !!entry.specificDate && entry.specificDate === isoDateOf(date);
  const isDayOfYear = !entry.specificDate && !!entry.dayOfYear && entry.dayOfYear === dayOfYearOf(date);
  const isWeekday = !entry.specificDate && !entry.dayOfYear && entry.dayOfWeek === SHORT_WEEKDAYS[date.getDay()];
  const isDefault = !entry.specificDate && !entry.dayOfYear && (entry.dayOfWeek ?? "*") === "*";
  if (isDate) return isStore ? 8 : 7;
  if (isDayOfYear) return isStore ? 6 : 5;
  if (isWeekday) return isStore ? 4 : 3;
  if (isDefault) return isStore ? 2 : 1;
  return 0;
}

/**
 * The opening hours for one date, or `undefined` when no row applies — the caller then falls back
 * to the store's static open/close times.
 */
export function resolveOpeningHours(entries: OpeningHoursEntry[], date: Date): ResolvedDay | undefined {
  let best: OpeningHoursEntry | undefined;
  let bestPriority = 0;
  for (const entry of entries) {
    const priority = priorityFor(entry, date);
    if (priority > bestPriority) {
      best = entry;
      bestPriority = priority;
    }
  }
  if (!best) return undefined;
  const start = timeToMinutes(best.openTime);
  const end = timeToMinutes(best.closeTime);
  // Both times null is how the table spells "closed all day"; a half-filled or inverted row is
  // dirty data, so treat it as no information rather than inventing a window.
  if (start === null || end === null) {
    return start === null && end === null ? { window: null, description: best.description ?? undefined } : undefined;
  }
  if (end <= start) return undefined;
  return { window: { start, end }, description: best.description ?? undefined };
}

/**
 * The opening hours a schedule exception's rule implies, or `undefined` when the answer isn't
 * unambiguous — either the rule spans days with different hours, or nothing is known about them.
 * An absent rule asks about every day, which is what the everyday schedule wants.
 */
export function resolveRuleOpeningHours(
  entries: OpeningHoursEntry[],
  rule: string | undefined,
  today: Date = new Date(),
): ResolvedDay | undefined {
  if (entries.length === 0) return undefined;
  const matches = compileRuleDays(rule);
  let resolved: ResolvedDay | undefined;
  let key: string | undefined;
  let sharedDescription: string | undefined;
  let first = true;

  for (let offset = 0; offset < HORIZON_DAYS; offset++) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
    if (!matches(date)) continue;
    const day = resolveOpeningHours(entries, date);
    // A day with no row falls back to the store default, which is a *different* answer from a day
    // with one — so it has to take part in the comparison rather than be skipped.
    const dayKey = day ? (day.window ? `${day.window.start}-${day.window.end}` : "closed") : "default";
    if (first) {
      resolved = day;
      key = dayKey;
      sharedDescription = day?.description;
      first = false;
      continue;
    }
    if (dayKey !== key) return undefined;
    if (day?.description !== sharedDescription) sharedDescription = undefined;
  }

  if (!resolved) return undefined;
  return { window: resolved.window, description: sharedDescription };
}

/** How a timeline should draw a day: its open window, plus the note explaining any deviation. */
export interface OpenHours {
  window?: CaptureWindow;
  note?: string;
}

/**
 * The opening hours to draw for a schedule whose rule is `expression` — the day-specific ones when
 * every day the rule can apply on agrees, otherwise `fallback` (the store's usual hours).
 */
export function openHoursFor(
  entries: OpeningHoursEntry[],
  expression: string | undefined,
  fallback?: CaptureWindow,
): OpenHours {
  const resolved = resolveRuleOpeningHours(entries, expression);
  if (!resolved) return { window: fallback };
  // A closed day is an empty window: no rail fill, and the backdrop shades the whole track.
  return { window: resolved.window ?? { start: 0, end: 0 }, note: resolved.description };
}
